// https://www.highcharts.com/demo/polar-spider
import {Options, Point, SeriesLineOptions, XAxisLabelsOptions, YAxisOptions} from 'highcharts';
import {XAxisOptions} from 'highcharts/highstock';
import {TFunction} from 'i18next';

import {ESQuestion} from '~src/interfaces/question.interface';
import {ContentLocalesI} from '~src/interfaces/session.types';
import {ESTopic} from '~src/interfaces/topic.interface';
import {translateContent} from '~src/services/translationServices';
import {
  isSurveyWithTopics,
  isTopicWithQuestions,
  SpiderChartDimensions,
} from '~src/store/spidergraph/spidergraph.reducer';
import {AnalysisLine} from '~src/modules/spidergraph/AnalysisLine';
import {Scale} from "~src/interfaces/scale.interface";
import {calcTwoDigitsRating, getScaleLength} from "~src/modules/reports/services/ratingTransformations";

// For our use, make some things mandatory that are optional for Highcharts
export type HighchartsSpiderWebOptions = Required<Pick<Options, 'series'>> & Omit<Options, 'series'>;

export function getDefaultOptions(): HighchartsSpiderWebOptions {
  return {
    chart: {
      polar: true,
      type: 'line',
    },
    credits: {
      enabled: false,
    },
    legend: {
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
    },
    pane: {
      size: '80%',
    },
    title: {
      text: 'Loopline Spider Graph',
      x: -80,
    },
    tooltip: {
      shared: true,
      pointFormatter: function(this: Point) {
        const scale = (this as PointValue).scale;
        let ratingString = this.y?.toFixed(2);
        if (scale) {
          ratingString = `${calcTwoDigitsRating(getScaleLength(scale), this.y!, scale.visualization)}\u202f/\u202f${calcTwoDigitsRating(getScaleLength(scale), 1, scale.visualization)}`;
        }
        return `<span style="color:${this.series.color?.toString()}">
            ${this.series.name}: <b>${ratingString}</b>
           </span><br/>`;
      },
    },
    exporting: {
      sourceWidth: 1280,
      sourceHeight: 768,
    },
    responsive: {
      rules: [
        {
          condition: { maxWidth: 1080 },
          chartOptions: {
            legend: {
              align: 'center',
              verticalAlign: 'bottom',
              layout: 'horizontal',
            },
            pane: {
              size: '70%',
            },
          },
        },
        {
          condition: { maxWidth: 500 },
          chartOptions: {
            legend: {
              align: 'center',
              verticalAlign: 'bottom',
              layout: 'vertical',
            },
            pane: {
              size: '60%',
            },
          },
        },
      ],
    },
    series: [],
    xAxis: {
      categories: [],
      tickmarkPlacement: 'on',
      lineWidth: 0,
      labels: {
        useHTML: true,
        formatter: function() {
          return `<span class="spider-corner">${this.value}</span>`;
        },
        events: {},
      } as XAxisLabelsOptions,
    } as XAxisOptions,
    yAxis: {
      gridLineInterpolation: 'polygon',
      lineWidth: 0,
      min: 0,
      max: 1,
      minorTickInterval: 0.2,
      labels: {
        enabled: false,
      },


      /*
      plotBands: [
        { from: 0.0, to: 0.2, color: '#ff4a7a' },
        { from: 0.2, to: 0.4, color: '#a879be' },
        { from: 0.4, to: 0.6, color: '#55a7ff' },
        { from: 0.6, to: 0.8, color: '#57c7f0' },
        { from: 0.8, to: 1.0, color: '#58e8e0' },
      ],
       */
    },
  };
}

interface PointValue {
  y: number;
  scale?: Scale;
}

function isTopic(e: ESTopic|ESQuestion): e is ESTopic {
  return Boolean((e as ESTopic).scale_id && !(e as ESQuestion).topic_id);
}

class SpiderChartOptions {
  private options = getDefaultOptions();

  constructor(private dimensions: SpiderChartDimensions,
              private scales: Scale[],
              private t: TFunction
  ) {
    this.setDimensions(dimensions);
    this.adjustOptionsToScale();
  }

  getOptions(): HighchartsSpiderWebOptions {
    return this.options;
  }

  addLine(line: AnalysisLine) {
    const edges = this.getEdges();
    // const scaleFactor = this.scale ? (this.scale.denominator + 1) : 1;
    const values: Array<PointValue | null> = edges.map((edge: ESTopic | ESQuestion) => {
      const point = line.points.find(value => value.edgeId === edge.id);
      let topic: ESTopic | undefined;
      const d = this.dimensions;
      if (isSurveyWithTopics(d) && isTopic(edge)) {
        topic = d.topics.find(t => t.id === edge.id);
      }
      if (isTopicWithQuestions(d)) {
        topic = d.topic;
      }
      const scale = this.scales.find(s => s.id === topic?.scale_id);
      return point ? {
      y: (point.value /** scaleFactor*/),
        scale: scale,
      } : null;
    });

    const seriesOptions: SeriesLineOptions = {
      data: values,
      name: line.getLabel(this.t),
      type: 'line',
      color: line.getColor(),
      id: line.getLineId(),
      legendIndex: this.options.series.length,
      connectNulls: true,
    };
    this.options.series.push(seriesOptions);
  }

  private setTitle(title: string | ContentLocalesI) {
    if (this.options.title) {
      this.options.title.text = typeof title === 'string' ? title : translateContent(title);
    }
  }

  private adjustOptionsToScale() {
    if (this.scales.length === 0) {
      return;
    }
    if (this.scales.length === 1) {
      const max = calcTwoDigitsRating(getScaleLength(this.scales[0]), 1, this.scales[0].visualization);
      (this.options.yAxis as YAxisOptions).minorTickInterval = 1 / max;
    } else {
      (this.options.yAxis as YAxisOptions).minorTicks = false;
    }
  }

  private setDimensions(value: SpiderChartDimensions) {
    this.dimensions = value;
    if (isTopicWithQuestions(value)) {
      this.setTitle(value.topic && value.topic.name);
    }
    if (isSurveyWithTopics(value)) {
      this.setTitle(value.survey && value.survey.name);
    }
    const collection = this.getEdges();
    if (collection) {
      this.setEdges(collection.map(item => translateContent('name' in item ? item.name : item.text)));
    }
  }

  private getEdges(): ESTopic[] | ESQuestion[] {
    if (!this.dimensions) {
      return [];
    }
    if (isSurveyWithTopics(this.dimensions)) {
      return this.dimensions.topics;
    }
    if (isTopicWithQuestions(this.dimensions)) {
      return this.dimensions.questions;
    }
    return [];
  }

  private setEdges(edges: string[]) {
    (this.options.xAxis as XAxisOptions).categories = edges;
  }
}

export default SpiderChartOptions;
